import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { formatLongDate } from '@/shared/lib/date-format';
import { cn } from '@/lib/utils';

/**
 * CalendarDateControl — the product's ONE day-picker affordance.
 *
 * A trigger that reads the selected day, a chevron, and a popover holding the
 * month grid with the blue "Today" link in its footer. Nothing else: no
 * prev/next arrows (those belong to whatever toolbar frames it, because their
 * STEP is view-dependent — a week view steps seven days), no view toggle, no
 * store.
 *
 * WHY IT EXISTS AS A COMPONENT (PROD-2408). This markup was inline inside
 * `CalendarSubToolbar`, which also owns the status pills, the invoice filter,
 * the location buttons and the view toggle — all of them wired to
 * `useSchedulingStore`. So the scheduling page's date control could not be
 * reused anywhere without dragging that whole toolbar in. The Technician
 * Worklist needed a day picker, and the choice was between a second date
 * idiom on a second surface or lifting this one out. It is lifted out; the
 * sub-toolbar now renders it, so there is exactly one popover, one month grid
 * and one "Today" link in the product and a change to any of them lands on
 * both surfaces at once.
 *
 * It is DELIBERATELY not the Signal DS `DatePicker` (which `DatePickerField`
 * adapts for forms): that atom is a labelled form field with a
 * `"YYYY-MM-DD"`-shaped value contract and a year dropdown spanning
 * 1900–2100. This is a toolbar affordance whose trigger reads as prose
 * ("Today", "September 18, 2026"), which is what both calling surfaces draw.
 *
 * The month grid is PORTALLED (the shared Popover), not rendered inline. Inline,
 * any ancestor with overflow clip/hidden/auto cut it off: on Calendar › Device
 * Sessions the .list-table card showed only the first week row (PROD-3316). Dismissal
 * (outside click, Escape) is owned by the Popover.
 */

export interface CalendarDateControlProps {
  /** The selected day. Time-of-day is ignored. */
  value: Date;
  /** Fires with the picked day. The popover closes itself. */
  onChange: (date: Date) => void;
  /**
   * Overrides the trigger's text. Omit for the long date; the Technician
   * Worklist passes "Today" on the current day, which is what its design
   * draws (artifact "Technician Screen Options", section 01).
   *
   * The popover's footer always shows the long date, so a short or relative
   * label never leaves the user unable to find out which day they are on.
   */
  label?: string;
  /** Applied to the positioning wrapper. */
  className?: string;
  /** Applied to the trigger button, on top of its own classes. */
  triggerClassName?: string;
}

export function CalendarDateControl({
  value,
  onChange,
  label,
  className,
  triggerClassName,
}: CalendarDateControlProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className={cn('relative', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          aria-haspopup="dialog"
          render={
            <button
              type="button"
              className={cn(
                'flex items-center gap-1 px-2 py-1 rounded hover:bg-grey-100 text-sm font-medium text-text-primary',
                triggerClassName,
              )}
            />
          }
        >
          {label ?? formatLongDate(value)}
          <ChevronDown className="h-3 w-3 text-text-muted" aria-hidden />
        </PopoverTrigger>

        <PopoverContent
          align="start"
          className="w-auto gap-0 bg-surface-card border border-border-default rounded-lg shadow-lg p-2"
        >
          <Calendar
            mode="single"
            // Transparent so the white `bg-surface-card` wrapper shows through.
            // Without this the Calendar's own `bg-background` (#F7F9FA grey)
            // renders, making this popover grey.
            className="bg-transparent"
            selected={value}
            // Open on the selected day's month, not today's. The content
            // unmounts on close, so this re-reads `value` on every open.
            defaultMonth={value}
            onSelect={(d) => {
              if (d) {
                onChange(d);
                setOpen(false);
              }
            }}
            showOutsideDays
            fixedWeeks
          />
          {/* Footer matches the Signal-DS DatePicker: blue "Today" link + value.
              The aria-label disambiguates it from a TRIGGER that also reads
              "Today" — which is exactly what the worklist header draws. */}
          <div className="mt-2 flex items-center justify-between border-t border-border-default pt-2 text-size-sm">
            <button
              type="button"
              aria-label="Go to today"
              onClick={() => {
                onChange(new Date());
                setOpen(false);
              }}
              className="font-medium text-brand-primary hover:underline"
            >
              Today
            </button>
            <span className="text-text-muted">{formatLongDate(value)}</span>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
